import type { TFunction } from 'i18next'

import { civilDateToInputValue, isValidCivilDate } from '~/models/civilDate'

/**
 * 清算書の件名 (JEPX が発行した原文) の、 英語表示用の訳
 * (`docs/specs/bidding/jepx-settlement.md` §1 ビジネスルール・AC-JST-001・付録「表示文言 (英語)」)。
 *
 * 件名は JEPX の原文が正で、 **画面の表示だけ**を英語表示の時に限って既知の 6 種で訳す。
 * 行データ (`JepxSettlement.title`) は原文のまま保つ (AC-JST-010 が原文で判定する。
 * CSV・PDF の件名も原文)。 この関数は表示用の文字列を返すだけで、 行データを書き換えない。
 */

type KnownSubjectKind =
  | 'spotTradeAmount'
  | 'intradayTradeAmount'
  | 'spotTradingFee'
  | 'intradayTradingFee'
  | 'indirectRightsFee'
  | 'flatRateFee'

/**
 * 既知の 6 種の名前 → 種別。 **空白を除いた形**で引く。
 *
 * 各種別の名前は 2 通り: spec の呼び名 (「スポット市場 売買代金」等) と、 JEPX が件名に使う
 * 呼び名 (スポット市場 = 「翌日取引」、 時間前市場 = 「時間前取引」。 例: 「翌日取引売買代金
 * 2023 年 12 月 8 日受渡分」はスポット市場 売買代金)。 間接送電権・定額制は 1 通り。
 */
const KNOWN_SUBJECT_KINDS: ReadonlyMap<string, KnownSubjectKind> = new Map([
  ['スポット市場売買代金', 'spotTradeAmount'],
  ['翌日取引売買代金', 'spotTradeAmount'],
  ['時間前市場売買代金', 'intradayTradeAmount'],
  ['時間前取引売買代金', 'intradayTradeAmount'],
  ['スポット市場売買手数料', 'spotTradingFee'],
  ['翌日取引売買手数料', 'spotTradingFee'],
  ['時間前市場売買手数料', 'intradayTradingFee'],
  ['時間前取引売買手数料', 'intradayTradingFee'],
  ['間接送電権手数料', 'indirectRightsFee'],
  ['定額制手数料', 'flatRateFee'],
])

/** 末尾の日付: 「{受渡日}受渡分」「{約定日}約定分」。 件名は空白を除いてから見るので間の空白は問わない。 */
const TRAILING_DATE_PATTERN =
  /^(.*?)(\d{4})年(\d{1,2})月(\d{1,2})日(受渡分|約定分)$/

const DATE_LABEL_BY_SUFFIX = {
  受渡分: 'delivery',
  約定分: 'trade',
} as const

type ParsedSubject = {
  kind: KnownSubjectKind
  /** 末尾に日付がある時だけ。 `iso` は YYYY-MM-DD (英語の日付の書式、 english-ui-text-guide.md「日付・時刻」)。 */
  date?: { label: 'delivery' | 'trade'; iso: string }
}

/**
 * 件名が既知の 6 種に当たるかを判定する。 当たらなければ `undefined` (= 既知でない件名)。
 *
 * 判定は「件名から空白と末尾の日付を除いた残り」が 6 種の名前 (JEPX の呼び名を含む) に一致するか。
 * 末尾の日付が**読めない**件名 (暦に無い日付 = 2026 年 2 月 30 日など。 全角数字・
 * 「2026-10-06」のような別の書式・「月だけ」の表記は日付として認識されず、 残りが 6 種の
 * 名前に一致しないので同じく対象外) は、 推測で訳さず既知でないものとして扱う。
 */
function parseKnownSubject(title: string): ParsedSubject | undefined {
  const compact = title.replace(/\s+/g, '')
  const match = TRAILING_DATE_PATTERN.exec(compact)

  if (match === null) {
    const kind = KNOWN_SUBJECT_KINDS.get(compact)
    return kind === undefined ? undefined : { kind }
  }

  const [, name, year, month, day, suffix] = match
  const kind = KNOWN_SUBJECT_KINDS.get(name)
  if (kind === undefined) return undefined

  const civil = { year: Number(year), month: Number(month), day: Number(day) }
  if (!isValidCivilDate(civil)) return undefined

  return {
    kind,
    date: {
      label: DATE_LABEL_BY_SUFFIX[suffix as keyof typeof DATE_LABEL_BY_SUFFIX],
      iso: civilDateToInputValue(civil),
    },
  }
}

/**
 * 清算書の件名の表示文字列。
 *
 * - 日本語表示 (`isEnglish = false`): 常に原文のまま。
 * - 英語表示: 既知の 6 種は spec 付録の英語にし、 末尾に日付があれば後ろに
 *   `(delivery date: YYYY-MM-DD)` / `(trade date: YYYY-MM-DD)` を添える
 *   (例: `Day-ahead market trade amount (delivery date: 2023-12-08)`)。
 *   既知でない件名は英語表示でも日本語の原文のまま。
 *
 * 英語の文言は i18n (`page:jepxSettlement.subject.*`) が持つ。
 */
export function formatSettlementSubject(
  title: string,
  isEnglish: boolean,
  t: TFunction,
): string {
  if (!isEnglish) return title

  const parsed = parseKnownSubject(title)
  if (parsed === undefined) return title

  const name = t(`page:jepxSettlement.subject.known.${parsed.kind}`)
  if (parsed.date === undefined) return name

  const date = t(`page:jepxSettlement.subject.date.${parsed.date.label}`, {
    date: parsed.date.iso,
  })
  return `${name} ${date}`
}
